import * as React from "react"
import { Wallet, TrendingUp, TrendingDown, PiggyBank, AlertCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { SummaryCard } from "../molecules/SummaryCard"
import { SkeletonCard } from "@/shared/ui/molecules/SkeletonCard"
import { useDashboardSummary } from "../../hooks/useDashboard"
import { Alert, AlertDescription } from "@/shared/ui/atoms/Alert"

interface SummaryGridProps {
  amountsVisible?: boolean
  year?: number
  month?: number
}

export const SummaryGrid = React.memo(function SummaryGrid({
  amountsVisible = true,
  year,
  month,
}: SummaryGridProps) {
  const summaryParams = React.useMemo(() => {
    if (year !== undefined && month !== undefined) {
      return { year, month }
    }
    return undefined
  }, [year, month])

  const { data, isLoading, isFetching, isError } = useDashboardSummary(summaryParams)

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>Failed to load dashboard summary. Please try refreshing.</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className={cn("cartera-summary-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-4 transition-opacity duration-200", isFetching && "opacity-70")}>
      <SummaryCard 
        title="General Available" 
        amount={data.general_available} 
        currency={data.currency}
        icon={<Wallet className="size-4" />} 
        valueClassName="text-accent-success"
        amountsVisible={amountsVisible}
      />
      <SummaryCard 
        title="Total Saved" 
        amount={data.total_saved} 
        currency={data.currency}
        icon={<PiggyBank className="size-4 text-accent-warning" />} 
        valueClassName="text-accent-warning"
        amountsVisible={amountsVisible}
      />
      <SummaryCard 
        title="Month Income" 
        amount={data.month_income} 
        currency={data.currency}
        icon={<TrendingUp className="size-4 text-text-primary" />} 
        valueClassName="text-text-primary"
        amountsVisible={amountsVisible}
      />
      <SummaryCard 
        title="Month Expenses" 
        amount={data.month_expenses} 
        currency={data.currency}
        icon={<TrendingDown className="size-4 text-accent-danger" />} 
        valueClassName="text-accent-danger"
        amountsVisible={amountsVisible}
      />
    </div>
  )
})

export default SummaryGrid

